import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { FormProvider, useForm, UseFormReturn } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import z from 'zod';

import {
  FundingRequestTypeEnumGQLType,
  Maybe,
  useGetResolvedLoanForEditLoanByIdQuery,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { LoanActivity, getInitialLoanActivity } from './activity';
import * as types from './types';

const RouteParamsStruct = z.object({
  loanId: z.string({ message: 'Missing URL parameter' }),
});

export type EditLoanFormFields = {
  externalLoanId?: Maybe<string>;
  pricingOptionKey: string;
  fundingRequests: types.FundingRequestsFormState;
};

type LoanMetadata = {
  pricingOptionKey: string;
  externalLoanId?: Maybe<string>;
};

type EditContextValue = {
  initial: {
    resolvedLoan: types.ResolvedLoanForEditLoan;
  };
  // resolved loan FR + invoice activity that drives loan activity table
  // transformed on initial resolved loan fetch
  activity: LoanActivity[];
  // @jendevelops still trying to decide if need to pass in form methods from context, or just pass with FormProvider
  formMethods: UseFormReturn<EditLoanFormFields>;
  submission: {
    isValid: boolean;
    // loan metadata
    loan: LoanMetadata;
    // invoices
    invoices: any[];
  };
};

type EditLoanContextType =
  | {
      status: 'mounting';
      state: null;
    }
  | {
      status: 'loading';
      state: null;
    }
  | {
      status: 'ready';
      state: EditContextValue;
    };

const mountingContext: EditLoanContextType = {
  status: 'mounting',
  state: null,
};

const loadingContext: EditLoanContextType = {
  status: 'loading',
  state: null,
};

const _createFundingRequestComponentsFormState = (
  fundingRequestComponents: types.ResolvedLoanForEditLoan['funding']['requests'][number]['fundingComponents'],
): types.FundingRequestComponentsState => {
  return fundingRequestComponents.reduce(
    (acc, { fundingRequestComponentId, requestedAmount, lenderShareId }) => {
      // eslint-disable-next-line no-param-reassign
      acc[fundingRequestComponentId] = {
        id: fundingRequestComponentId,
        amount: requestedAmount,
        lenderShareId,
      };
      return acc;
    },
    {} as types.FundingRequestComponentsState,
  );
};

const _createFundingRequestFormState = (
  fundingRequests: types.ResolvedLoanForEditLoan['funding']['requests'],
): types.FundingRequestsFormState => {
  return fundingRequests.reduce(
    (
      acc,
      {
        fundingRequestId,
        fundingComponents,
        totalRequestedAmount,
        fundingRequestType,
      },
    ) => {
      // eslint-disable-next-line no-param-reassign
      acc[fundingRequestId] = {
        id: fundingRequestId,
        amount: totalRequestedAmount,
        type: fundingRequestType,
        components: _createFundingRequestComponentsFormState(fundingComponents),
      };
      return acc;
    },
    {} as types.FundingRequestsFormState,
  );
};

const _formFieldsToSubmissionPayload = (
  formFields: EditLoanFormFields,
): {
  originalFundingRequestIds: string[];
  updatedFundingRequests: {
    // TODO needs funding date
    amount: number;
    type: FundingRequestTypeEnumGQLType;
    fundingRequestComponents: {
      amount: number;
      lenderShareId: string;
      // assuming fr component type is draw, but need to update if ever support recycle as well
    }[];
  }[];
} => {
  return {
    originalFundingRequestIds: Object.values(formFields.fundingRequests).map(
      (fundingRequest) => fundingRequest.id,
    ),
    updatedFundingRequests: Object.values(formFields.fundingRequests).map(
      (fundingRequest) => ({
        amount: fundingRequest.amount,
        type: fundingRequest.type,
        fundingRequestComponents: Object.values(fundingRequest.components).map(
          (component) => ({
            amount: component.amount,
            lenderShareId: component.lenderShareId,
          }),
        ),
      }),
    ),
  };
};

const EditLoanContext = createContext<EditLoanContextType>(mountingContext);

export const useEditLoanContext = () => useContext(EditLoanContext);

export const EditLoanProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const params = useParams();
  const { loanId } = RouteParamsStruct.parse(params);

  const [state, setState] = useState<EditLoanContextType>(mountingContext);
  const formMethods = useForm<EditLoanFormFields>({
    mode: 'all',
    reValidateMode: 'onBlur',
  });

  const {
    data,
    loading: isLoading,
    error,
  } = useGetResolvedLoanForEditLoanByIdQuery({
    variables: {
      loanId,
    },
    fetchPolicy: 'no-cache',
  });

  const onSubmit = useCallback((formFields: EditLoanFormFields) => {
    // eslint-disable-next-line no-console
    console.log('form fields', formFields);

    const payload = _formFieldsToSubmissionPayload(formFields);

    // eslint-disable-next-line no-console
    console.log('payload', payload);
  }, []);

  useEffect(() => {
    if (isLoading) {
      setState(loadingContext);
    }
    if (data) {
      formMethods.reset({
        externalLoanId: data.resolvedLoan.externalLoanId,
        pricingOptionKey: data.resolvedLoan.pricing.key,
        fundingRequests: _createFundingRequestFormState(
          data.resolvedLoan.funding.requests,
        ),
      });
      setState({
        status: 'ready',
        state: {
          initial: {
            resolvedLoan: data.resolvedLoan,
          },
          activity: getInitialLoanActivity({ resolvedLoan: data.resolvedLoan }),
          formMethods,
          submission: {
            isValid: formMethods.formState.isValid,
            loan: {
              externalLoanId: data.resolvedLoan.externalLoanId,
              pricingOptionKey: data.resolvedLoan.pricing.key,
            },
            invoices: [],
          },
        },
      });
    }
  }, [data, isLoading, formMethods]);

  if (error) {
    throw error;
  }

  return (
    <EditLoanContext.Provider value={state}>
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(onSubmit)}>{children}</form>
      </FormProvider>
    </EditLoanContext.Provider>
  );
};
